import React, { useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import "./App.scss";
import GratefulDB from "./GratefulDB";
import { GratefulDBContextProvider } from "./GratefulDBContext";
import MenuBar from "./components/bars/MenuBar";
import PlayerBar from "./components/bars/PlayerBar";
import { useAudioControls } from "./components/shared/useAudioControls";
import { useLoading } from "./components/shared/useLoading";
import { usePassword } from "./components/shared/usePassword";
import Chords from "./components/views/Chords";
import Comics from "./components/views/Comics";
import Favorites from "./components/views/Favorites";
import HomeScreen from "./components/views/HomeScreen";
import LoadingScreen from "./components/views/LoadingScreen";
import MobilePlayer from "./components/views/MobilePlayer";
import PasswordScreen from "./components/views/PasswordScreen";
import Radio from "./components/views/Radio";
import SearchResults from "./components/views/SearchResults";
import ShowResult from "./components/views/ShowResult";
import TimeMachine from "./components/views/TimeMachine";
import YearResults from "./components/views/YearResults";

const gratefulDB = new GratefulDB();

const App: React.FC = () => {
  const loading = useLoading();
  const location = useLocation();
  const password = usePassword();
  const audio = React.useRef<HTMLAudioElement>(null);
  const audioControls = useAudioControls(audio, gratefulDB);
  const progress = React.useRef<HTMLDivElement>(null);
  const [showPlayerBar, setShowPlayerBar] = useState<boolean>();

  useEffect(() => {
    if (loading.loaded()) {
      return;
    }
    gratefulDB.init(
      loading,
      (config) => {
        // @ts-ignore
        $(progress.current).progress({
          duration: 0,
          total: config.chunks,
          label: "",
        });
      },
      () => {
        // @ts-ignore
        $(progress.current).progress("increment");
      }
    );
  }, []);

  useEffect(() => {
    if (!loading.loaded()) {
      return;
    }
    audioControls.checkPersistedAudioState();
  }, [loading.loaded()]);

  useEffect(() => {
    setShowPlayerBar(
      audioControls.isVisible() && !location.pathname.startsWith("/player")
    );
  }, [location, audioControls]);

  if (
    process.env.REACT_APP_PASSWORD_REQUIRED === "true" &&
    !password.isAllowed()
  ) {
    return <PasswordScreen />;
  }

  if (!loading.loaded()) {
    return <LoadingScreen progressRef={progress} />;
  }

  return (
    <div className={`App ${showPlayerBar ? "player-active" : ""}`}>
      <audio preload="auto" ref={audio} onEnded={audioControls.nextTrack} />
      <GratefulDBContextProvider value={gratefulDB}>
        <MenuBar loading={loading} />
        <Routes>
          <Route path="/" element={<HomeScreen />} />
          <Route
            path="show/:entryId"
            element={<ShowResult audioControls={audioControls} />}
          />
          <Route path="search/:term" element={<SearchResults />} />
          <Route path="search/:year/:term" element={<SearchResults />} />
          <Route path="year/:year" element={<YearResults />} />
          <Route
            path="player"
            element={<MobilePlayer audioControls={audioControls} />}
          />
          <Route
            path="timemachine"
            element={
              <TimeMachine
                audioControls={audioControls}
                chunkConfig={gratefulDB.chunkConfig()}
              />
            }
          />
          <Route
            path="radio"
            element={
              <Radio
                audioControls={audioControls}
                chunkConfig={gratefulDB.chunkConfig()}
              />
            }
          />
          <Route path="favorites" element={<Favorites />} />
          <Route path="chords" element={<Chords />} />
          <Route path="chords/:id" element={<Chords />} />
          <Route path="comics" element={<Comics />} />
        </Routes>
        {showPlayerBar ? <PlayerBar audioControls={audioControls} /> : null}
      </GratefulDBContextProvider>
    </div>
  );
};

export default App;
